import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { StateService } from "../../../services/state.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Item } from "../../../pojo/Item";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import {
    TrailApi,
    type AvailabilityOperator,
    type AvailabilityRuleInput,
    type CurriculumDetail,
    type FocusAreaDto,
} from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { dropdownItemFromId, idFromDropdownItem } from "./trail.dropdown.util";
import { nextAppendSortOrder, uniqueSortOrdersForInsertSet } from "../../../util/sort.order.util";
import {
    editorNodeToSaveTree,
    wireTreeToEditorNode,
    type RequirementGroupNode,
    type RequirementNode,
} from "./trail.requirement.tree.component";
import { TrailRequirementTreeEditorComponent } from "./trail.requirement.tree.editor.component";
import { TrailCurriculumTracksComponent } from "./trail.curriculum.tracks.component";

type CurriculumDraft = Omit<CurriculumDetail, "requirementTree"> & {
    // Frontend editor: same tree in the discriminated UI node shape used by s25-ng-trail-requirement-tree.
    requirementTree?: RequirementGroupNode;
};

@TypeManagerDecorator("s25-ng-trail-curriculum-editor")
@Component({
    selector: "s25-ng-trail-curriculum-editor",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25MultiselectModule,
        S25DropdownPaginatedModule,
        TrailRequirementTreeEditorComponent,
        TrailCurriculumTracksComponent,
    ],
    host: { class: "trail-curriculum-editor" },
    template: `
        <div class="c-padding--double">
            <div class="trail-curriculum-editor__header c-margin-bottom--single">
                <h2>{{ labels.CURRICULUM }} Editor</h2>
                <s25-ng-button [type]="'outline'" [onClick]="handleBack">← Back</s25-ng-button>
            </div>

            @if (curriculumResource.isLoading()) {
                <p>Loading…</p>
            } @else if (curriculumResource.error()) {
                <p class="c-text--error">{{ formatError(curriculumResource.error()) }}</p>
            } @else if (draft()) {
                <div class="trail-curriculum-editor__status-bar c-margin-bottom--single">
                    <span
                        class="trail-curriculum-editor__status-badge"
                        [class.trail-curriculum-editor__status-badge--draft]="draft()!.status === 'DRAFT'"
                        [class.trail-curriculum-editor__status-badge--published]="draft()!.status === 'PUBLISHED'"
                        [class.trail-curriculum-editor__status-badge--disabled]="draft()!.status === 'DISABLED'"
                    >
                        {{ statusLabel(draft()!.status) }}
                    </span>
                </div>

                <form class="trail-curriculum-editor__form" (ngSubmit)="$event.preventDefault(); saveCurriculum()">
                    <div class="form-group c-margin-bottom--single">
                        <label>Name *</label>
                        <input
                            class="c-input"
                            name="name"
                            [ngModel]="draft()!.name"
                            (ngModelChange)="patchDraft('name', $event)"
                            required
                        />
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>Display Name</label>
                        <input
                            class="c-input"
                            name="displayName"
                            [ngModel]="draft()!.displayName ?? ''"
                            (ngModelChange)="patchDraft('displayName', $event)"
                        />
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>Description</label>
                        <textarea
                            class="c-input"
                            name="description"
                            rows="3"
                            [ngModel]="draft()!.description ?? ''"
                            (ngModelChange)="patchDraft('description', $event)"
                        ></textarea>
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>{{ labels.CATALOG }} Description</label>
                        <textarea
                            class="c-input"
                            name="catalogDescription"
                            rows="3"
                            [ngModel]="draft()!.catalogDescription ?? ''"
                            (ngModelChange)="patchDraft('catalogDescription', $event)"
                            placeholder="Shown on the catalog card"
                        ></textarea>
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>{{ labels.ENDING_AWARD }} Key</label>
                        <input
                            class="c-input"
                            name="endingAwardKey"
                            [ngModel]="draft()!.endingAwardKey ?? ''"
                            (ngModelChange)="patchDraft('endingAwardKey', $event)"
                            [disabled]="draft()!.status === 'PUBLISHED'"
                        />
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>{{ labels.ENDING_AWARD }} Display Name</label>
                        <input
                            class="c-input"
                            name="endingAwardDisplayName"
                            [ngModel]="draft()!.endingAwardDisplayName ?? ''"
                            (ngModelChange)="patchDraft('endingAwardDisplayName', $event)"
                        />
                    </div>
                </form>

                <section class="c-margin-top--double">
                    <h3>{{ labels.ENDING_AWARD }} criteria</h3>
                    <p class="c-text--secondary">
                        Students earn the {{ labels.ENDING_AWARD }} by satisfying this tree, including
                        {{ labels.SUMMIT }} leaves for required {{ labels.TRAILS.toLowerCase() }}.
                    </p>
                    @if (requirementTree() && draft()!.cohortId != null) {
                        <s25-ng-trail-requirement-tree-editor
                            [cohortId]="draft()!.cohortId"
                            [node]="requirementTree()"
                            [editable]="draft()!.status !== 'DISABLED'"
                            [domain]="'COCURRICULAR'"
                            (nodeChange)="onTreeChange($event)"
                        />
                    } @else {
                        <p class="c-text--secondary">No {{ labels.ENDING_AWARD.toLowerCase() }} tree defined.</p>
                        @if (draft()!.status !== "DISABLED") {
                            <s25-ng-button [type]="'outline'" [onClick]="initializeTree">
                                Initialize {{ labels.ENDING_AWARD }} tree
                            </s25-ng-button>
                        }
                    }
                </section>

                <section class="c-margin-top--double">
                    <h3>Availability Rules</h3>
                    <p class="c-text--secondary">
                        Custom attribute rules that determine student eligibility. Students see ineligibility reasons in
                        the {{ labels.CATALOG.toLowerCase() }}.
                    </p>
                    @for (rule of draft()!.availabilityRules ?? []; track $index; let ruleIndex = $index) {
                        <div class="trail-curriculum-editor__avail-rule c-margin-bottom--half">
                            <div class="form-group">
                                <label>Custom attribute *</label>
                                <s25-custom-attr-dropdown
                                    [itemTypeId]="contactItemTypeId"
                                    [ariaLabel]="'Custom attribute'"
                                    [value]="dropdownItemFromId(rule.custAtrbId)"
                                    (valueChange)="onAvailRuleCustAtrbChange(ruleIndex, $event)"
                                />
                            </div>
                            <div class="form-group">
                                <label>Operator *</label>
                                <select
                                    class="c-input"
                                    [ngModel]="rule.operator"
                                    (ngModelChange)="onAvailRuleOperatorChange(ruleIndex, $event)"
                                >
                                    <option value="IN">In</option>
                                    <option value="NOT_IN">Not In</option>
                                    <option value="CONTAINS">Contains</option>
                                    <option value="EXISTS">Exists</option>
                                    <option value="NOT_EXISTS">Not Exists</option>
                                </select>
                            </div>
                            @if (rule.operator !== "EXISTS" && rule.operator !== "NOT_EXISTS") {
                                @if (rule.operator === "CONTAINS") {
                                    <div class="form-group">
                                        <label>Value *</label>
                                        <input
                                            class="c-input"
                                            data-id="rule-contains-value"
                                            [attr.aria-label]="'Value'"
                                            [ngModel]="(rule.values ?? [])[0] ?? ''"
                                            (ngModelChange)="patchContainsValue(ruleIndex, $event)"
                                        />
                                    </div>
                                } @else {
                                    <div class="form-group">
                                        <label>Values *</label>
                                        <div class="trail-curriculum-editor__rule-values">
                                            @for (value of rule.values ?? []; track $index; let valueIndex = $index) {
                                                <div class="trail-curriculum-editor__rule-value">
                                                    <input
                                                        class="c-input"
                                                        data-id="rule-in-value"
                                                        [attr.aria-label]="'Value ' + (valueIndex + 1)"
                                                        [ngModel]="value"
                                                        (ngModelChange)="
                                                            patchRuleValueAt(ruleIndex, valueIndex, $event)
                                                        "
                                                    />
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        data-id="rule-in-remove"
                                                        [ariaLabel]="'Remove value'"
                                                        (clicked)="removeRuleValue(ruleIndex, valueIndex)"
                                                    >
                                                        Remove
                                                    </s25-ng-button>
                                                </div>
                                            }
                                            <s25-ng-button
                                                [type]="'outline'"
                                                data-id="rule-in-add"
                                                (clicked)="addRuleValue(ruleIndex)"
                                            >
                                                Add value
                                            </s25-ng-button>
                                        </div>
                                    </div>
                                }
                            }
                            <div class="form-group">
                                <label>Ineligibility Description</label>
                                <input
                                    class="c-input"
                                    [ngModel]="rule.catalogDescription ?? ''"
                                    (ngModelChange)="patchAvailRule(ruleIndex, 'catalogDescription', $event)"
                                />
                            </div>
                            @if (draft()!.status === "DRAFT") {
                                <s25-ng-button [type]="'outline'" (clicked)="removeAvailRule(ruleIndex)"
                                    >Remove</s25-ng-button
                                >
                            }
                        </div>
                    }
                    @if (draft()!.status === "DRAFT") {
                        <s25-ng-button data-id="add-avail-rule" [type]="'outline'" [onClick]="addAvailRule">
                            Add Rule
                        </s25-ng-button>
                    }
                </section>

                @if (draft()!.defnId && draft()!.cohortId != null) {
                    <s25-ng-trail-curriculum-tracks [defnId]="draft()!.defnId" [cohortId]="draft()!.cohortId!" />
                }

                <section class="trail-curriculum-editor__actions c-margin-top--double">
                    <s25-ng-button [type]="'primary'" [onClick]="saveCurriculum">
                        Save {{ labels.CURRICULUM }}
                    </s25-ng-button>
                    @if (draft()!.status === "DRAFT") {
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonClass]="'aw-button--primary'"
                            [onClick]="publishCurriculum"
                        >
                            Publish
                        </s25-ng-button>
                    }
                    @if (draft()!.status === "PUBLISHED") {
                        <s25-ng-button [type]="'danger--outline'" [onClick]="disableCurriculum">
                            Disable
                        </s25-ng-button>
                    }
                    <div class="trail-curriculum-editor__preview">
                        <label>{{ labels.PREVIEW_STUDENT_OPTIONAL }}</label>
                        <p class="c-text--secondary trail-curriculum-editor__preview-hint">
                            {{ labels.PREVIEW_STUDENT_HINT }}
                        </p>
                        <div class="trail-curriculum-editor__preview-picker">
                            <s25-ng-multiselect-search-criteria
                                [type]="'contacts'"
                                [buttonText]="'Search contacts'"
                                [modelBean]="previewContactBean"
                                [selectedItems]="previewContactBean.selectedItems ?? []"
                                [showResults]="true"
                                [popoverOnBody]="true"
                                [useSecurity]="false"
                                [singleSelect]="true"
                                (close)="onPreviewContactClose($event)"
                            />
                            @if (previewContactId()) {
                                <s25-ng-button [type]="'outline'" (clicked)="clearPreviewContact()"
                                    >Clear</s25-ng-button
                                >
                            }
                        </div>
                        <a class="trail-curriculum-editor__preview-link" (click)="previewAsStudent()">
                            {{ labels.PREVIEW_AS_STUDENT }}
                        </a>
                    </div>
                </section>
            }
        </div>
    `,
    styles: `
        .trail-curriculum-editor__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
        }
        .trail-curriculum-editor__status-bar {
            display: flex;
            align-items: center;
        }
        .trail-curriculum-editor__status-badge {
            display: inline-block;
            padding: 0.2rem 0.6rem;
            border-radius: 4px;
            font-size: 0.85em;
            font-weight: 600;
        }
        .trail-curriculum-editor__status-badge--draft {
            background: #fef3cd;
            color: #856404;
        }
        .trail-curriculum-editor__status-badge--published {
            background: #d4edda;
            color: #155724;
        }
        .trail-curriculum-editor__status-badge--disabled {
            background: #f8d7da;
            color: #721c24;
        }
        .trail-curriculum-editor__form .form-group {
            display: grid;
            grid-template-columns: 14rem minmax(0, 1fr);
            gap: 0.75rem;
            align-items: start;
            max-width: 48rem;
        }
        .trail-curriculum-editor__form .form-group > label {
            padding-top: 0.35rem;
        }
        .trail-curriculum-editor__actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 1rem;
        }
        .trail-curriculum-editor__preview {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.35rem;
        }
        .trail-curriculum-editor__preview-hint {
            margin: 0;
            max-width: 28rem;
        }
        .trail-curriculum-editor__preview-picker {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }
        .trail-curriculum-editor__preview-link {
            cursor: pointer;
            color: var(--color--link-text, #0066cc);
            text-decoration: underline;
        }
        .trail-curriculum-editor__avail-rule {
            border: 1px solid var(--color--border, #ddd);
            border-radius: 6px;
            padding: 0.75rem 1rem;
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem;
            align-items: flex-start;
        }
        .trail-curriculum-editor__rule-values {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.35rem;
        }
        .trail-curriculum-editor__rule-value {
            display: flex;
            gap: 0.35rem;
            align-items: center;
        }
        .trail-curriculum-editor__avail-rule .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
        }
        /* Host class ngEditableCustomAttrSelector is globally position:absolute. */
        s25-custom-attr-dropdown.ngEditableCustomAttrSelector {
            position: relative;
            display: block;
            width: min(100%, 20rem);
        }
        .trail-curriculum-editor__avail-rule .c-input {
            min-width: 8rem;
        }
    `,
})
export class TrailCurriculumEditorComponent {
    readonly labels = TrailLabels;
    readonly defnId = input.required<number>();
    readonly backToList = output<void>();

    private readonly reloadGen = signal(0);
    readonly previewContactId = signal<number | null>(null);
    readonly previewContactName = signal("");
    readonly contactItemTypeId = Item.Ids.Contact;
    readonly dropdownItemFromId = dropdownItemFromId;

    previewContactBean: MultiselectModelI = {
        uuid: "trail-curriculum-editor-preview-contact",
        singleSelect: true,
        title: "Contacts",
        selectedItems: [],
    };

    readonly curriculumResource = s25Resource({
        params: () => ({ defnId: this.defnId(), gen: this.reloadGen() }),
        loader: async ({ params }) => {
            const raw = await TrailApi.getCurriculum(params.defnId);
            return {
                ...raw,
                requirementTree: wireTreeToEditorNode(raw.requirementTree, raw.cohortId),
            } as CurriculumDraft;
        },
    });

    readonly focusAreasResource = s25Resource({
        loader: () => TrailApi.getFocusAreas(),
    });

    readonly focusAreaOptions = computed((): FocusAreaDto[] => {
        return this.focusAreasResource.value() ?? [];
    });

    readonly draft = linkedSignal<CurriculumDraft | null>(() => {
        const loaded = this.curriculumResource.value();
        return loaded ? { ...loaded } : null;
    });

    readonly requirementTree = computed((): RequirementGroupNode | null => {
        return this.draft()?.requirementTree ?? null;
    });

    statusLabel(status?: string): string {
        switch (status) {
            case "DRAFT":
                return TrailLabels.DRAFT;
            case "PUBLISHED":
                return TrailLabels.PUBLISHED;
            case "DISABLED":
                return TrailLabels.DISABLED;
            default:
                return status ?? "";
        }
    }

    formatError(error: unknown): string {
        if (error instanceof Error) return error.message;
        return String(error ?? "Unknown error");
    }

    patchDraft<K extends keyof CurriculumDraft>(key: K, value: CurriculumDraft[K]) {
        const current = this.draft();
        if (!current) return;
        this.draft.set({ ...current, [key]: value });
    }

    onTreeChange(updatedTree: RequirementNode) {
        if (updatedTree.nodeType === "group") {
            this.patchDraft("requirementTree", updatedTree as RequirementGroupNode);
        }
    }

    initializeTree = () => {
        const rootGroup: RequirementGroupNode = {
            nodeType: "group",
            groupType: "ALL",
            label: "Root",
            children: [],
        };
        this.patchDraft("requirementTree", rootGroup);
    };

    patchAvailRule<K extends keyof AvailabilityRuleInput>(ruleIndex: number, key: K, value: AvailabilityRuleInput[K]) {
        const current = this.draft();
        if (!current?.availabilityRules) return;
        const updatedRules = current.availabilityRules.map((rule, idx) =>
            idx === ruleIndex ? { ...rule, [key]: value } : rule,
        );
        this.patchDraft("availabilityRules", updatedRules);
    }

    onAvailRuleCustAtrbChange(ruleIndex: number, choice: DropDownItem | null): void {
        this.patchAvailRule(ruleIndex, "custAtrbId", idFromDropdownItem(choice));
    }

    onAvailRuleOperatorChange(ruleIndex: number, operator: AvailabilityOperator): void {
        const current = this.draft();
        if (!current?.availabilityRules) return;
        const updatedRules = current.availabilityRules.map((rule, idx) => {
            if (idx !== ruleIndex) {
                return rule;
            }
            if (operator === "EXISTS" || operator === "NOT_EXISTS") {
                return { ...rule, operator, values: [] };
            }
            if (operator === "CONTAINS") {
                const firstValue = (rule.values ?? []).map((value) => value.trim()).find(Boolean) ?? "";
                return { ...rule, operator, values: [firstValue] };
            }
            const existingValues = rule.values ?? [];
            const nextValues = existingValues.length ? [...existingValues] : [""];
            return { ...rule, operator, values: nextValues };
        });
        this.patchDraft("availabilityRules", updatedRules);
    }

    patchContainsValue(ruleIndex: number, value: string): void {
        this.patchAvailRule(ruleIndex, "values", [value]);
    }

    patchRuleValueAt(ruleIndex: number, valueIndex: number, value: string): void {
        const current = this.draft();
        if (!current?.availabilityRules) return;
        const updatedRules = current.availabilityRules.map((rule, idx) => {
            if (idx !== ruleIndex) {
                return rule;
            }
            const values = [...(rule.values ?? [])];
            values[valueIndex] = value;
            return { ...rule, values };
        });
        this.patchDraft("availabilityRules", updatedRules);
    }

    addRuleValue(ruleIndex: number): void {
        const current = this.draft();
        if (!current?.availabilityRules) return;
        const updatedRules = current.availabilityRules.map((rule, idx) =>
            idx === ruleIndex ? { ...rule, values: [...(rule.values ?? []), ""] } : rule,
        );
        this.patchDraft("availabilityRules", updatedRules);
    }

    removeRuleValue(ruleIndex: number, valueIndex: number): void {
        const current = this.draft();
        if (!current?.availabilityRules) return;
        const updatedRules = current.availabilityRules.map((rule, idx) => {
            if (idx !== ruleIndex) {
                return rule;
            }
            const values = [...(rule.values ?? [])];
            values.splice(valueIndex, 1);
            return { ...rule, values };
        });
        this.patchDraft("availabilityRules", updatedRules);
    }

    removeAvailRule(ruleIndex: number) {
        const current = this.draft();
        if (!current?.availabilityRules) return;
        this.patchDraft(
            "availabilityRules",
            current.availabilityRules.filter((_, idx) => idx !== ruleIndex),
        );
    }

    addAvailRule = () => {
        const current = this.draft();
        if (!current) return;
        const existingRules = current.availabilityRules ?? [];
        const newRule: AvailabilityRuleInput = {
            operator: "IN",
            values: [""],
            catalogDescription: "",
            sortOrder: nextAppendSortOrder(existingRules.map((rule) => rule.sortOrder)),
        };
        this.patchDraft("availabilityRules", [...existingRules, newRule]);
    };

    private cleanedRule(rule: AvailabilityRuleInput): AvailabilityRuleInput {
        if (rule.operator === "EXISTS" || rule.operator === "NOT_EXISTS") {
            return { ...rule, values: [] };
        }
        const values = (rule.values ?? []).map((value) => value.trim()).filter(Boolean);
        if (rule.operator === "CONTAINS") {
            return { ...rule, values: values.slice(0, 1) };
        }
        return { ...rule, values };
    }

    handleBack = () => {
        this.backToList.emit();
    };

    saveCurriculum = async () => {
        const current = this.draft();
        if (!current) return;
        if (current.status === "PUBLISHED") {
            const confirmed = await AlertService.confirm(
                "This curriculum is published. Students may already be in progress. Are you sure you want to modify the requirement tree?",
            );
            if (!confirmed) return;
        }
        const incompleteRules = (current.availabilityRules ?? []).some(
            (rule) => rule.custAtrbId == null || !Number.isFinite(rule.custAtrbId),
        );
        if (incompleteRules) {
            await AlertService.alert("Each rule needs a custom attribute.");
            return;
        }
        const cleanedRules = (current.availabilityRules ?? []).map((rule) => this.cleanedRule(rule));
        const uniqueOrders = uniqueSortOrdersForInsertSet(cleanedRules.map((rule) => rule.sortOrder));
        const rulesToSave = cleanedRules.map((rule, ruleIndex) => ({
            ...rule,
            sortOrder: uniqueOrders[ruleIndex],
        }));
        try {
            await TrailApi.updateCurriculum(current.defnId, {
                name: current.name,
                displayName: current.displayName,
                description: current.description,
                catalogDescription: current.catalogDescription,
                endingAwardKey: current.endingAwardKey,
                endingAwardDisplayName: current.endingAwardDisplayName,
                catalogSortOrder: current.catalogSortOrder,
                requirementTree: current.requirementTree
                    ? editorNodeToSaveTree(current.requirementTree, current.cohortId)
                    : undefined,
                availabilityRules: rulesToSave,
            });
            this.backToList.emit();
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    publishCurriculum = async () => {
        const current = this.draft();
        if (!current) return;
        if (!(await AlertService.confirm(`Publish this ${TrailLabels.CURRICULUM}? Students will be able to see it.`))) {
            return;
        }
        try {
            await TrailApi.publishCurriculum(current.defnId);
            this.reloadGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    disableCurriculum = async () => {
        const current = this.draft();
        if (!current) return;
        if (!(await AlertService.confirm(`Disable this ${TrailLabels.CURRICULUM}? It will no longer be visible.`))) {
            return;
        }
        try {
            await TrailApi.disableCurriculum(current.defnId);
            this.backToList.emit();
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    onPreviewContactClose(selected: S25ItemI[] | undefined): void {
        const items = selected ?? [];
        const first = items[0];
        const contactItemId = first?.itemId != null ? Number(first.itemId) : null;
        this.previewContactId.set(Number.isFinite(contactItemId) ? contactItemId : null);
        this.previewContactName.set(first?.itemName ?? "");
        this.previewContactBean.selectedItems = [...items];
    }

    clearPreviewContact = () => {
        this.previewContactId.set(null);
        this.previewContactName.set("");
        this.previewContactBean.selectedItems = [];
    };

    previewAsStudent = () => {
        const current = this.draft();
        if (!current) return;
        const previewContactId = this.previewContactId();
        if (previewContactId != null) {
            void StateService.go(
                "seTrailAdminProgress",
                {
                    defnId: current.defnId,
                    contId: previewContactId,
                    contactName: this.previewContactName(),
                },
                {},
                true,
            );
            return;
        }
        void StateService.go("seMyTrails", { defnId: current.defnId }, {}, true);
    };
}
